import { Card, FormGroup, FormLabel, InputGroup, FormControl, Button } from "react-bootstrap"
import FormRange from "react-bootstrap/esm/FormRange"
import { SectionIndicators } from "./Common"
import { getFinancialMetrics } from "../Utilities/calculations"
import { ResultProps } from "../Types/calculator"
import { incrementDecrementArrows } from "../Utilities/interface"
import { getTwoDigitLocaleString } from "../Utilities/constants"

export function Results(props: ResultProps) {
  const { state, setSettings } = props
  const { crashMitigationEffectiveness } = state
  const { netPresentValue, irr, paybackPeriod, dollarsSaved, dollarsSpent } = getFinancialMetrics(state)

  return (
    <Card>
      <Card.Header tabIndex={80} as="h3">
        Results
        <SectionIndicators complete={false} tooltipMessage="Here is where more information about the results section can go." />
      </Card.Header>
      <Card.Body>
        <FormGroup>
          Below are estimates for the net present value, return on investment, and payback period for adopting this technology
          using a 5-year timeframe. Please use the sliding bar to adjust the assumptions about crash mitigation to see how it
          impacts the potential ROI.
        </FormGroup>
        <FormGroup>
          <FormLabel aria-label="Crash Mitigation Effectiveness">
            Crash Mitigation Effectiveness: <strong>{Math.round(crashMitigationEffectiveness * 100)}%</strong>
            <FormRange
              tabIndex={81}
              onKeyDown={(ev) => incrementDecrementArrows({ ev, field: "crashMitigationEffectiveness", percent: true, state, setSettings })}
              className="ExcludeFromExport"
              min={0}
              max={1}
              step={0.01}
              onChange={(ev) => setSettings({ crashMitigationEffectiveness: Number(ev.target.value) })}
              value={crashMitigationEffectiveness}
            />
          </FormLabel>
        </FormGroup>
        <FormGroup>
          Adjusting the sliders or going back and changing information in the previous tabs will automatically update the results
          below:
        </FormGroup>
        <br />
        <FormGroup>
          <FormLabel aria-label="Net Present Value">
            Net Present Value
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <FormControl readOnly value={!Number.isNaN(netPresentValue) ? netPresentValue.toLocaleString() : " - "} />
            </InputGroup>
          </FormLabel>
        </FormGroup>
        <FormGroup>
          <FormLabel aria-label="ROI">
            ROI
            <InputGroup>
              <FormControl readOnly value={!Number.isNaN(irr) ? irr : " - "} />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </FormLabel>
        </FormGroup>
        <FormGroup>
          <FormLabel aria-label="Payback Period">
            Payback Period (years)
            <FormControl readOnly value={!Number.isNaN(paybackPeriod) ? paybackPeriod : " - "} />
          </FormLabel>
        </FormGroup>
        <FormGroup>
          <FormLabel aria-label="Total Dollars Saved">
            Total Dollars Saved (over 5 years)
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <FormControl readOnly value={!Number.isNaN(dollarsSaved) ? getTwoDigitLocaleString(dollarsSaved) : " - "} />
            </InputGroup>
          </FormLabel>
        </FormGroup>
        <FormGroup>
          <FormLabel aria-label="Total Dollars Spent">
            Total Dollars Spent (over 5 years)
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <FormControl readOnly value={!Number.isNaN(dollarsSpent) ? getTwoDigitLocaleString(dollarsSpent) : " - "} />
            </InputGroup>
          </FormLabel>
        </FormGroup>
        <div>
          The above are estimates based on the inputs you provided about your fleet and some assumptions about how you operate.
          The analysis focuses on the ROI generated from a reduction in the number and severity of crashes. The analysis doesn't
          include long-term factors such as reductions in your insurance premiums. The analysis also doesn't include potential
          benefits for crashes that do occur, such as reduced litigation costs. The ROI calculator is intended as a guide for
          decision-making for rapidly-evolving heavy vehicle ADAS.
        </div>
        <Button
          className="ExcludeFromExport"
          style={{ marginTop: "1rem" }}
          onClick={() => window.print()}
          variant="primary"
          tabIndex={82}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && window.print()}
        >
          Print
        </Button>
      </Card.Body>
    </Card>
  )
}